import { Box, Button } from "@mui/material";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { TWChip, TWChipColor } from "../TWChip/TWChip";
import { useState, useRef, useCallback, useEffect } from "react";

export type TWTableAction = {
  id: string;
  label: string;
  onClick: () => void;
};
type TWTableActionsProps = {
  selectedCount: number;
  onClearSelection: () => void;
  actions: TWTableAction[];
};

export const TWTableActions = ({ selectedCount, onClearSelection, actions }: TWTableActionsProps) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    // Initialize position from current computed position if not set
    if (position === null) {
      setPosition({ x: rect.left, y: rect.top });
    }
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      cleanupRef.current = null;
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Store cleanup function to avoid memory leaks if unmount happens while dragging
    cleanupRef.current = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [position]);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  // Initial centered bottom position vs dragged custom position
  const positionStyles = position === null
    ? { left: "50%", transform: "translateX(-50%)", bottom: "5%" }
    : { left: position.x, top: position.y, transform: "none" };

  return (
    <Box
      ref={boxRef}
      sx={{
        position: "absolute",
        ...positionStyles,
        cursor: isDragging ? "grabbing" : "default",
        userSelect: isDragging ? "none" : "auto",
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: "flex", gap: 2, backgroundColor: "black", maxWidth: "fit-content", height: 52, p: 1, px: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", pr: 2, borderRight: "1px solid gray", alignItems: "center" }}>
          <DragIndicatorIcon
            sx={{ color: "white", mr: 2, cursor: "grab", "&:active": { cursor: "grabbing" } }}
            onMouseDown={handleMouseDown}
          />
          <Button variant="contained" color="primary" sx={{ cursor: "pointer" }} onClick={onClearSelection}>
            Clear Selected
          </Button>
          <TWChip chipColor={TWChipColor.blue} label={selectedCount.toString()} />
        </Box>
        {actions.map((action) => (
          <Button key={action.id} variant="contained" color="primary" sx={{ cursor: "pointer" }} onClick={action.onClick}>
            {action.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};
